// Template registry — merges the 7 built-ins shipped in @frame/shared with
// any user-saved templates persisted in localStorage. v1 keeps user-saved
// templates per-browser; the SQLite-backed `templates` table from spec §10
// returns post-launch.
//
// Storage layout (single key, single JSON array):
//   localStorage['frame-user-templates'] = [Template, Template, ...]
//
// We validate the deserialized payload defensively — a corrupt or out-of-
// version write shouldn't break the home page. Anything that doesn't
// shape-check is ignored.

import {
  BUILT_IN_TEMPLATES,
  type Shape,
  type Template,
  type TemplateMeta,
} from '@frame/shared';

const STORAGE_KEY = 'frame-user-templates';

export function listBuiltInTemplates(): readonly Template[] {
  return BUILT_IN_TEMPLATES;
}

export function listUserTemplates(): Template[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidTemplate);
  } catch {
    return [];
  }
}

// All templates the picker should show, built-ins first then user-saved in
// most-recently-created order. Built-ins live in a fixed order so the picker
// reads the same on every device.
export function listTemplates(): Template[] {
  const users = listUserTemplates();
  users.sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
  return [...BUILT_IN_TEMPLATES, ...users];
}

export function findTemplate(id: string): Template | null {
  return listTemplates().find((t) => t.id === id) ?? null;
}

// Persist a new user-saved template. `meta.id` is generated if absent; pass
// a deterministic id from tests via opts.id. Returns the saved Template so
// callers can navigate / show it immediately.
export interface SaveTemplateInput {
  name: string;
  description?: string;
  shapes: Shape[];
  id?: string;
}

export function saveUserTemplate(input: SaveTemplateInput): Template {
  const id = input.id ?? `user-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const meta: TemplateMeta = {
    id,
    kind: 'user',
    name: input.name.trim() || 'Untitled',
    description: (input.description ?? '').trim(),
    createdAt: Date.now(),
  };
  // Strip arrow-binding shapeId refs that point outside the saved set — the
  // bbox tool doesn't follow shape ids out of the snapshot, and a dangling
  // ref would render as a "missing" arrow on next instantiate.
  const validIds = new Set(input.shapes.map((s) => s.id));
  const shapes = input.shapes.map((s) => {
    if (s.type !== 'arrow') return s;
    return {
      ...s,
      from: stripDanglingEndpoint(s.from, validIds),
      to: stripDanglingEndpoint(s.to, validIds),
    };
  });
  const template: Template = { ...meta, shapes };
  const existing = listUserTemplates();
  // Replace by id if it already exists (idempotent save under tests).
  const next = [...existing.filter((t) => t.id !== id), template];
  writeStorage(next);
  return template;
}

export function deleteUserTemplate(id: string): void {
  const existing = listUserTemplates();
  const next = existing.filter((t) => t.id !== id);
  writeStorage(next);
}

function writeStorage(templates: Template[]): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
  } catch {
    // Quota exceeded / private mode — silently no-op.
  }
}

function isValidTemplate(t: unknown): t is Template {
  if (!t || typeof t !== 'object') return false;
  const tpl = t as Partial<Template>;
  if (typeof tpl.id !== 'string') return false;
  if (typeof tpl.kind !== 'string') return false;
  if (typeof tpl.name !== 'string') return false;
  if (!Array.isArray(tpl.shapes)) return false;
  return true;
}

function stripDanglingEndpoint(
  ep: { kind: 'point'; x: number; y: number } | { kind: 'shape'; shapeId: string },
  validIds: Set<string>,
): { kind: 'point'; x: number; y: number } | { kind: 'shape'; shapeId: string } {
  if (ep.kind !== 'shape') return ep;
  if (validIds.has(ep.shapeId)) return ep;
  // Fallback to a point at origin — the user can re-anchor on next edit.
  return { kind: 'point', x: 0, y: 0 };
}
